import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const serviceLinks = [
  { label: "Local Moving", href: "/local-moving" },
  { label: "Residential Moving", href: "/residential-moving" },
  { label: "Commercial Moving", href: "/commercial-moving" },
  { label: "Apartment Moving", href: "/apartment-moving" },
  { label: "Storage In and Out", href: "/storage-in-and-out" },
  { label: "Packing Service", href: "/packing-service" },
  { label: "Packers and Movers", href: "/packers-and-movers" },
  { label: "Labor-only Moving", href: "/labor-only-moving" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const handleServicesEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setServicesOpen(true);
  };

  const handleServicesLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-sm shadow-md"
          : "bg-background-50 shadow-sm"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start leading-tight">
            <span className="text-base md:text-lg font-extrabold tracking-wider text-accent-500 uppercase">
              NW TRUCKING
            </span>
            <span className="text-xs md:text-sm font-extrabold tracking-wider text-accent-500 uppercase -mt-1">
              OF NC LLC
            </span>
            <span className="text-[9px] md:text-[10px] font-semibold tracking-[0.25em] text-foreground-500 uppercase -mt-0.5">
              TRUCKING SERVICES
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive("/")
                  ? "text-accent-500"
                  : "text-foreground-700 hover:text-accent-500"
              }`}
            >
              {isActive("/") && <span className="block h-0.5 bg-accent-500 mt-0.5 rounded-full"></span>}
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
            >
              <button className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors flex items-center gap-1 whitespace-nowrap">
                Services
                <i className="ri-arrow-down-s-line text-xs" />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-xl border border-background-200 py-2 z-50">
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:text-accent-500 hover:bg-background-100 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about-us"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive("/about-us")
                  ? "text-accent-500"
                  : "text-foreground-700 hover:text-accent-500"
              }`}
            >
              {isActive("/about-us") && <span className="block h-0.5 bg-accent-500 mt-0.5 rounded-full"></span>}
              About Us
            </Link>

            <Link
              to="/contact-us"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive("/contact-us")
                  ? "text-accent-500"
                  : "text-foreground-700 hover:text-accent-500"
              }`}
            >
              {isActive("/contact-us") && <span className="block h-0.5 bg-accent-500 mt-0.5 rounded-full"></span>}
              Contact Us
            </Link>
          </nav>

          {/* Phone & Yelp */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:2295087357"
              className="text-sm font-semibold text-accent-500 hover:text-accent-600 transition-colors whitespace-nowrap"
            >
              Call Us: (229) 508-7357
            </a>
            <a
              href="https://www.yelp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-500 hover:text-accent-600 transition-colors"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="fab fa-yelp text-lg" />
              </div>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/"
              className={`block text-sm font-medium py-2 ${
                isActive("/") ? "text-accent-500" : "text-foreground-700"
              }`}
            >
              Home
            </Link>

            <div>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 py-2"
              >
                Services
                <i className={`ri-arrow-down-s-line transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 space-y-2 mt-1">
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="block text-sm text-foreground-600 hover:text-accent-500 py-1"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about-us"
              className={`block text-sm font-medium py-2 ${
                isActive("/about-us") ? "text-accent-500" : "text-foreground-700"
              }`}
            >
              About Us
            </Link>
            <Link
              to="/contact-us"
              className={`block text-sm font-medium py-2 ${
                isActive("/contact-us") ? "text-accent-500" : "text-foreground-700"
              }`}
            >
              Contact Us
            </Link>

            <a
              href="tel:2295087357"
              className="block text-sm font-semibold text-accent-500 py-2"
            >
              Call Us: (229) 508-7357
            </a>
          </div>
        </div>
      )}
    </header>
  );
}